import {
  ConversationId,
  ConversationThreadId,
  MessageAttachmentId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { Attachment } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/attachment";
import { useRequiredContext } from "@redotech/react-util/context";
import { useAsyncDebounce } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { toast } from "@redotech/redo-web/alert";
import { messageInputShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { Effect, Option } from "effect";
import { KEYS, type TElement, type Value } from "platejs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCloseConversationAction,
  useDeleteDraftAction,
  useMarkInProgressConversationAction,
  useSendMessageAction,
} from "../actions/use-conversation-actions";
import type { EditorToolbarMode } from "../components/message-editor/editor-plugins";
import { useMessageEditor } from "../components/message-editor/use-message-editor";
import { useTeamNotificationStream } from "../stream/team-notification-stream-provider";
import { usePublishDraftConversation } from "./use-create-conversation";
import { useDraft, useDraftMutations } from "./use-draft";
import { useImageUpload } from "./use-image-upload";
import { useNotifyTyping } from "./use-notify-presence";
import type { ReplyMode } from "./use-reply-mode";
import { useSendMessage } from "./use-send-message";

interface EmailRecipients {
  to: { email: string; name: string | null }[];
  cc: { email: string; name: string | null }[];
  bcc: { email: string; name: string | null }[];
}

function buildSaveDraftInput(
  base: {
    threadId: ConversationThreadId;
    inReplyToMessageId?: MessageId;
    content: string;
    attachments: Attachment[];
  },
  channel: MessageChannel,
  recipients?: EmailRecipients,
): SaveDraftInput {
  if (channel === "gmail" || channel === "outlook") {
    return {
      ...base,
      channel,
      channelData: {
        to: recipients?.to,
        cc: recipients?.cc,
        bcc: recipients?.bcc,
      },
    };
  }
  return { ...base, channel, channelData: null };
}

interface UseMessageInputStateOptions {
  conversationId: ConversationId;
  threadId: ConversationThreadId;
  editorMode: EditorToolbarMode;
  channel?: MessageChannel;
  /** Required for email (reply to specific message), optional for chat */
  inReplyToMessageId?: MessageId;
  /** Email recipients for the reply (from the message being replied to) */
  replyRecipients?: EmailRecipients;
  onDiscardDraft?: () => void;
  conversationStatus?: ConversationStatus;
  /** Required when conversationStatus === "DRAFT" to publish the conversation */
  draftPublishParams?: { customerEmail: string; subject: string };
  replyMode?: ReplyMode;
}

export function useMessageInputState({
  conversationId,
  threadId,
  editorMode,
  channel,
  inReplyToMessageId,
  replyRecipients,
  onDiscardDraft,
  conversationStatus,
  draftPublishParams,
  replyMode,
}: UseMessageInputStateOptions) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const editorId = `message-input-${threadId}-${inReplyToMessageId ?? "thread"}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const editorState = useMessageEditor(editorMode, editorId);
  const [isGeneratingAiResponse, setIsGeneratingAiResponse] = useState(false);
  const aiTextAccumulatorRef = useRef("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [draftContent, setDraftContent] = useState<string>("");
  const hasLoadedDraft = useRef(false);
  const isLoadingDraftRef = useRef(false);

  // Chat drafts are keyed per-thread (not per-reply-target) so the
  // optimistic SENDING state survives when a new inbound message arrives
  // and changes the latest message id. inReplyToMessageId is still written
  // to the draft at save time so the backend can set message.in_reply_to_id.
  const draftLookupInReplyToMessageId =
    editorMode === "email" ? inReplyToMessageId : undefined;

  const { data: draft } = useDraft({
    conversationId,
    threadId,
    inReplyToMessageId: draftLookupInReplyToMessageId,
  });

  const {
    saveDraft,
    isSavingDraft,
    ensureDraftId,
    deleteDraft,
    isDeletingDraft,
  } = useDraftMutations({
    conversationId,
    threadId,
    inReplyToMessageId: draftLookupInReplyToMessageId,
    existingDraftId: draft?.id ?? null,
  });

  const { sendMessage, isSending: isSendingMessage } = useSendMessage({
    conversationId,
  });
  const { publishDraftConversation, isPublishing } =
    usePublishDraftConversation();
  const isSending = isSendingMessage || isPublishing;
  const notifyTyping = useNotifyTyping(conversationId);

  const getDraftId = useCallback(() => draft?.id, [draft]);

  const ensureDraft = useCallback(async () => {
    return ensureDraftId();
  }, [ensureDraftId]);

  const { uploadImage } = useImageUpload({ getDraftId, ensureDraft });

  const handleImageSelect = useCallback(
    async (file: File) => {
      const url = await uploadImage(file);
      if (url && editorState.editor) {
        editorState.editor.tf.insertNodes(
          { type: KEYS.img, url, children: [{ text: "" }] },
          { removeEmpty: true },
        );
        editorState.editor.tf.focus();
      }
    },
    [uploadImage, editorState.editor],
  );

  const debouncedSaveDraft = useAsyncDebounce(
    useCallback(
      async (content: string, currentAttachments: Attachment[]) => {
        const plainText = editorState.getPlainText();
        const isEmpty = !plainText.trim() && currentAttachments.length === 0;
        if (isEmpty) return;

        await saveDraft(
          buildSaveDraftInput(
            {
              threadId,
              inReplyToMessageId,
              content,
              attachments: currentAttachments,
            },
            channel ?? "redoChat",
            replyRecipients,
          ),
        );
      },
      [
        threadId,
        inReplyToMessageId,
        editorState,
        saveDraft,
        replyRecipients,
        channel,
      ],
    ),
    500,
  );

  useEffect(() => {
    if (!draft || hasLoadedDraft.current || draft.status === "SENDING") return;

    try {
      // Skip setValue if the editor already has this content (e.g. the user
      // typed something and the first save just created the draft with that
      // same content). Calling setValue unnecessarily causes focus loss.
      if (draft.content !== draftContent) {
        const parsedValue = JSON.parse(draft.content) as Value;
        isLoadingDraftRef.current = true;
        editorState.setValue(parsedValue);
        setDraftContent(draft.content);
      }
      setAttachments([...draft.attachments]);
      hasLoadedDraft.current = true;
    } catch {
      toast("Failed to load draft", { variant: "error" });
    }
  }, [draft, editorState, inReplyToMessageId, draftContent]);

  const handleEditorChange = useHandler(({ value }: { value: Value }) => {
    if (isLoadingDraftRef.current) {
      isLoadingDraftRef.current = false;
      return;
    }
    const content = JSON.stringify(value);
    if (content === draftContent) return;
    setDraftContent(content);
    notifyTyping();
    void debouncedSaveDraft(content, attachments);
  });

  const handleFileSelect = useHandler((files: File[]) => {
    const newAttachments: Attachment[] = files.map((file) => ({
      id: MessageAttachmentId.generate(),
      url: URL.createObjectURL(file),
      description: Option.fromNullable(file.name),
      mimeType: Option.fromNullable(file.type),
    }));
    setAttachments((prev) => {
      const updated = [...prev, ...newAttachments];
      void debouncedSaveDraft(draftContent, updated);
      return updated;
    });
  });

  const handleRemoveAttachment = useCallback(
    (index: number) => {
      setAttachments((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        void debouncedSaveDraft(draftContent, updated);
        return updated;
      });
    },
    [draftContent, debouncedSaveDraft],
  );

  const closeAction = useCloseConversationAction();
  const markInProgressAction = useMarkInProgressConversationAction();

  const sendAndReset = useCallback(async (): Promise<boolean> => {
    if (!editorState.getPlainText() && attachments.length === 0) return false;

    const html = await editorState.getHtml();

    if (conversationStatus === "DRAFT") {
      if (!draftPublishParams?.customerEmail || !draftPublishParams?.subject) {
        toast("Customer email and subject are required to send", {
          variant: "error",
        });
        return false;
      }
      await publishDraftConversation({
        conversationId,
        customerEmail: draftPublishParams.customerEmail,
        subject: draftPublishParams.subject,
        content: html,
        attachments,
      });
    } else {
      // Email replies must target a specific parent message; chat threads
      // (SMS, Facebook DM, etc.) don't require one.
      if (editorMode === "email" && !inReplyToMessageId) return false;

      const sendChannel =
        replyMode === "internalNote" ? "internalNote" : channel;
      if (!sendChannel) return false;

      // Ensure a draft exists and save latest content before sending
      const draftId = draft?.id ?? (await ensureDraftId());
      if (!draftId) return false;
      await saveDraft(
        buildSaveDraftInput(
          { threadId, inReplyToMessageId, content: html, attachments },
          sendChannel,
          replyRecipients,
        ),
      );
      await sendMessage({ channel: sendChannel, threadId, draftId });
    }

    editorState.clearContent();
    setAttachments([]);
    setDraftContent("");
    hasLoadedDraft.current = true;
    onDiscardDraft?.();
    return true;
  }, [
    editorState,
    editorMode,
    attachments,
    inReplyToMessageId,
    sendMessage,
    threadId,
    draft,
    conversationStatus,
    draftPublishParams,
    publishDraftConversation,
    conversationId,
    onDiscardDraft,
    replyMode,
    channel,
    ensureDraftId,
    saveDraft,
    replyRecipients,
  ]);

  const isInternalNote = replyMode === "internalNote";

  const handleSend = useHandler(async () => {
    if (await sendAndReset()) {
      toast(isInternalNote ? "Internal note added" : "Message sent", {
        variant: "success",
      });
    }
  });

  const handleSendAndClose = useHandler(async () => {
    if (await sendAndReset()) {
      void closeAction.execute(conversationId);
      toast(
        isInternalNote
          ? "Internal note added and conversation closed"
          : "Message sent and conversation closed",
        { variant: "success" },
      );
    }
  });

  const handleSendAndMarkInProgress = useHandler(async () => {
    if (await sendAndReset()) {
      markInProgressAction.execute(conversationId);
      toast(
        isInternalNote
          ? "Internal note added and marked in progress"
          : "Message sent and marked in progress",
        { variant: "success" },
      );
    }
  });

  const handleDeleteDraft = useHandler(async () => {
    if (!draft?.id) {
      onDiscardDraft?.();
      return;
    }

    await deleteDraft(draft.id);
    editorState.clearContent();
    setAttachments([]);
    setDraftContent("");
    hasLoadedDraft.current = false;
    onDiscardDraft?.();
  });

  useTeamNotificationStream("aiTextDelta", (event) => {
    if (
      event.conversationId !== conversationId ||
      event.threadId !== threadId
    ) {
      return;
    }
    aiTextAccumulatorRef.current += event.textDelta;
    const value: TElement[] = [
      { type: "p", children: [{ text: aiTextAccumulatorRef.current }] },
    ];
    editorState.setValue(value);
  });

  useTeamNotificationStream("aiStatus", (event) => {
    if (
      event.conversationId !== conversationId ||
      event.threadId !== threadId
    ) {
      return;
    }
    if (event.status === "complete" || event.status === "error") {
      setIsGeneratingAiResponse(false);
    }
  });

  const handleGenerateAiResponse = useHandler(async () => {
    if (!effectApi || !channel) {
      return;
    }
    aiTextAccumulatorRef.current = "";
    setIsGeneratingAiResponse(true);
    try {
      const client = effectApi.getClient();
      await effectApi.runPromise(
        client.aiAgent
          .generateAiResponse({
            payload: { conversationId, threadId, channel, mode: "copilot" },
          })
          .pipe(Effect.orDie),
      );
    } catch (error) {
      console.error("AI response generation failed:", error);
      setIsGeneratingAiResponse(false);
    }
  });

  const sendAction = useSendMessageAction(handleSend);
  const deleteDraftAction = useDeleteDraftAction(handleDeleteDraft);

  useKeyboardShortcuts({
    bindings: useMemo(
      () => [
        { shortcut: messageInputShortcuts.SEND_MESSAGE, handler: handleSend },
        {
          shortcut: messageInputShortcuts.SEND_AND_CLOSE,
          handler: handleSendAndClose,
        },
        {
          shortcut: messageInputShortcuts.SEND_AND_MARK_IN_PROGRESS,
          handler: handleSendAndMarkInProgress,
        },
        {
          shortcut: messageInputShortcuts.DELETE_DRAFT,
          handler: handleDeleteDraft,
        },
      ],
      [
        handleSend,
        handleSendAndClose,
        handleSendAndMarkInProgress,
        handleDeleteDraft,
      ],
    ),
    scope: "messageInput",
    enabled: true,
    containerRef,
  });

  return {
    containerRef,
    editorState,
    attachments,
    draft,
    isGeneratingAiResponse,
    isSavingDraft,
    isDeletingDraft,
    isSending,
    sendAction,
    deleteDraftAction,
    handleEditorChange,
    handleFileSelect,
    handleGenerateAiResponse,
    handleImageSelect,
    handleRemoveAttachment,
    handleSend,
    handleSendAndClose,
    handleSendAndMarkInProgress,
    handleDeleteDraft,
  };
}
